import _ from "lodash";
import { useEffect, useState } from "react";

/**
 * Custom hook to calculate the number of columns in a grid, based on the container width and item width.
 *
 * @param {React.RefObject} containerRef - The ref of the container element.
 * @returns {number} The current number of columns.
 */

const DEBOUNCE_TIME = 75;

const useDynamicColumns = containerRef => {
  const [columns, setColumns] = useState(1);

  useEffect(() => {
    if (!containerRef?.current) return;

    const container = containerRef?.current;

    const calculateColumns = _.debounce(() => {
      const items = _.toArray(container?.children);

      if (_.size(items) === 0) {
        setColumns(1);
        return;
      }

      const firstItem = _.first(items);

      const containerClientWidth = _.get(container, "clientWidth", 0);
      const containerStyle = window.getComputedStyle(container);
      const containerLeftPadding = parseFloat(containerStyle?.paddingLeft) || 0;
      const containerRightPadding =
        parseFloat(containerStyle?.paddingRight) || 0;
      const containerWidth =
        containerClientWidth - containerLeftPadding - containerRightPadding;
      const gap = parseFloat(containerStyle?.columnGap) || 0;

      const itemStyle = window.getComputedStyle(firstItem);
      const itemOffsetWidth = _.get(firstItem, "offsetWidth", 0);
      const itemMarginRight = parseFloat(itemStyle?.marginRight) || 0;
      const itemMarginLeft = parseFloat(itemStyle?.marginLeft) || 0;

      const itemWidth = itemOffsetWidth + itemMarginRight + itemMarginLeft;

      const numberOfColumns = _.round(
        _.divide(containerWidth + gap, itemWidth + gap)
      );

      const calculatedColumns = _.max([1, numberOfColumns]);

      setColumns(calculatedColumns);
    }, DEBOUNCE_TIME);

    calculateColumns();

    const resizeObserver = new ResizeObserver(() => {
      calculateColumns();
    });

    resizeObserver.observe(container);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [containerRef]);

  return columns;
};

export default useDynamicColumns;
